/**
 * Hosted storefront (FR-WEB-01..06): the design a tenant edits in the builder, how delivery is
 * charged, and what a shopper sends at checkout. Shoppers pay cash on delivery only for now.
 */
import { z } from "zod";
import { findDistrictById } from "../geo";
import { bdMobileSchema } from "./auth";
import { poishaSchema } from "./inventory";
import { mediaKeySchema } from "./media";
import { publicQuantitySchema } from "./public-api";

/**
 * Signature themes: Bazaar (bold, for groceries and general shops), Boutique (editorial, for
 * fashion and electronics), Apothecary (calm, for pharmacies) and Catalog (show, don't sell).
 */
export const STOREFRONT_THEMES = ["bazaar", "boutique", "apothecary", "catalog"] as const;
export type StorefrontTheme = (typeof STOREFRONT_THEMES)[number];

/** Themes from before the redesign, read as their closest new theme; nothing needs migrating. */
const LEGACY_THEMES: Record<string, StorefrontTheme> = {
  general: "bazaar",
  grocery: "bazaar",
  pharmacy: "apothecary",
};
export const storefrontThemeSchema = z.preprocess(
  (v) => (typeof v === "string" ? (LEGACY_THEMES[v] ?? v) : v),
  z.enum(STOREFRONT_THEMES),
);

/** The theme a new shop starts with, from its business type. */
export function themeForPack(packId: string): StorefrontTheme {
  if (packId === "pharmacy") return "apothecary";
  if (packId === "fashion" || packId === "electronics") return "boutique";
  return "bazaar";
}

const text = (max: number) =>
  z
    .object({
      en: z.string().trim().max(max).default(""),
      bn: z.string().trim().max(max).default(""),
    })
    .default({ en: "", bn: "" });
export type LocalText = { en: string; bn: string };

/** Links inside the shop ("/c/…", "/p/…"), to other https sites, or tel:/mailto:. */
const hrefSchema = z
  .string()
  .trim()
  .max(300)
  .refine(
    (v) =>
      v === "" ||
      /^\/(?!\/)[^\s]*$/.test(v) ||
      /^https:\/\/[^\s]+$/.test(v) ||
      /^tel:\+?[0-9]{6,15}$/.test(v) ||
      /^mailto:[^\s@]+@[^\s@]+$/.test(v),
    "Use a page in the shop (starting with /), an https:// link, tel: or mailto:",
  );

const colorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a colour like #0f766e");

const sectionBase = {
  id: z.string().regex(/^[a-z0-9-]{1,40}$/),
  visible: z.boolean().default(true),
};

export const storefrontSectionSchema = z.discriminatedUnion("type", [
  z.object({
    ...sectionBase,
    type: z.literal("announcement"),
    text: text(140),
    href: hrefSchema.default(""),
  }),
  z.object({
    ...sectionBase,
    type: z.literal("hero"),
    /** Photo beside the words, photo behind them, or words on the brand colour. */
    layout: z.enum(["split", "full", "text"]).default("split"),
    heading: text(90),
    subheading: text(200),
    image: mediaKeySchema.nullable().default(null),
    ctaLabel: text(30),
    ctaHref: hrefSchema.default(""),
  }),
  z.object({
    ...sectionBase,
    type: z.literal("categories"),
    title: text(60),
    /** Empty shows every category that has items online. */
    categoryIds: z.array(z.string().max(64)).max(24).default([]),
  }),
  z.object({
    ...sectionBase,
    type: z.literal("products"),
    title: text(60),
    source: z.enum(["newest", "category", "manual"]).default("newest"),
    categoryId: z.string().max(64).nullable().default(null),
    itemIds: z.array(z.string().max(64)).max(24).default([]),
    limit: z.number().int().min(2).max(24).default(8),
  }),
  z.object({
    ...sectionBase,
    type: z.literal("banner"),
    image: mediaKeySchema.nullable().default(null),
    heading: text(90),
    body: text(240),
    href: hrefSchema.default(""),
  }),
  z.object({
    ...sectionBase,
    type: z.literal("text"),
    title: text(90),
    /** Plain text; blank lines start new paragraphs. No HTML. */
    body: text(4000),
  }),
  z.object({
    ...sectionBase,
    type: z.literal("highlights"),
    /** Up to four short promises, e.g. "Cash on delivery". Empty fills them from the shop's settings. */
    items: z
      .array(z.object({ title: text(40), body: text(100) }))
      .max(4)
      .default([]),
  }),
  z.object({
    ...sectionBase,
    type: z.literal("contact"),
    title: text(60),
    showPhone: z.boolean().default(true),
    showAddress: z.boolean().default(true),
    hours: text(120),
  }),
]);
export type StorefrontSection = z.output<typeof storefrontSectionSchema>;
export type StorefrontSectionType = StorefrontSection["type"];

export const storefrontDesignSchema = z.object({
  theme: storefrontThemeSchema.default("bazaar"),
  primaryColor: colorSchema.default("#0f766e"),
  logo: mediaKeySchema.nullable().default(null),
  tagline: text(120),
  /** Language shoppers see first; they can switch. */
  defaultLocale: z.enum(["bn", "en"]).default("bn"),
  sections: z.array(storefrontSectionSchema).max(30).default([]),
  seo: z
    .object({ title: text(70), description: text(160) })
    .default({ title: { en: "", bn: "" }, description: { en: "", bn: "" } }),
  social: z
    .object({
      facebook: z
        .string()
        .trim()
        .max(200)
        .refine((v) => v === "" || /^https:\/\/(www\.|m\.)?(facebook|fb)\.com\//.test(v), {
          message: "Use your Facebook page link",
        })
        .default(""),
      instagram: z
        .string()
        .trim()
        .max(200)
        .refine((v) => v === "" || /^https:\/\/(www\.)?instagram\.com\//.test(v), {
          message: "Use your Instagram link",
        })
        .default(""),
      whatsapp: z.union([z.literal(""), bdMobileSchema]).default(""),
    })
    .default({ facebook: "", instagram: "", whatsapp: "" }),
  footerNote: text(300),
});
export type StorefrontDesign = z.output<typeof storefrontDesignSchema>;
export type StorefrontDesignInput = z.input<typeof storefrontDesignSchema>;

/** How delivery is charged (FR-WEB-04). Inside Dhaka means Dhaka district. */
export const deliveryRatesSchema = z.object({
  insideDhaka: poishaSchema.default(6_000),
  outsideDhaka: poishaSchema.default(12_000),
  /** Per-district charges that override the two defaults. */
  districts: z
    .record(
      z.string().refine((id) => findDistrictById(id) !== null, "Unknown district"),
      poishaSchema,
    )
    .default({}),
  /** Orders at or above this subtotal ship free; null turns it off. */
  freeAbove: poishaSchema.nullable().default(null),
});
export type DeliveryRates = z.output<typeof deliveryRatesSchema>;

export const storefrontOpsSchema = z.object({
  /** The branch whose stock the shop sells; null uses the main branch. */
  branchId: z.string().max(64).nullable().default(null),
  delivery: deliveryRatesSchema.default({
    insideDhaka: 6_000,
    outsideDhaka: 12_000,
    districts: {},
    freeAbove: null,
  }),
  /** Text the shopper a code before an order is accepted. Uses the shop's SMS credits. */
  verifyPhone: z.boolean().default(false),
  minOrder: poishaSchema.default(0),
  hideOutOfStock: z.boolean().default(false),
  /** Shoppers can sign in with a phone code to see their orders. Each code uses one SMS credit. */
  shopperAccounts: z.boolean().default(false),
  /** Shown on the checkout page, e.g. delivery times. */
  checkoutNote: text(300),
});
export type StorefrontOps = z.output<typeof storefrontOpsSchema>;
export type StorefrontOpsInput = z.input<typeof storefrontOpsSchema>;

export function deliveryChargeFor(
  rates: DeliveryRates,
  districtId: string,
  subtotal: number,
): number {
  if (rates.freeAbove !== null && subtotal >= rates.freeAbove) return 0;
  const special = rates.districts[districtId];
  if (special !== undefined) return special;
  return districtId === "dhaka" ? rates.insideDhaka : rates.outsideDhaka;
}

/** Names that would collide with platform hosts or confuse shoppers. */
const RESERVED_SLUGS = new Set([
  "www",
  "app",
  "api",
  "admin",
  "mail",
  "shop",
  "store",
  "help",
  "support",
  "status",
  "docs",
  "blog",
  "static",
  "media",
  "cdn",
  "zeeta",
  "zeetabyte",
  "inventory",
  "customers",
]);

export const storefrontSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/,
    "Use 3 to 40 letters, digits or hyphens, starting and ending with a letter or digit",
  )
  .refine((v) => !v.includes("--"), "Do not use two hyphens in a row")
  .refine((v) => !RESERVED_SLUGS.has(v), "This address is reserved");

export const storefrontSaveSchema = z
  .object({
    design: storefrontDesignSchema.optional(),
    ops: storefrontOpsSchema.optional(),
  })
  .refine((v) => v.design !== undefined || v.ops !== undefined, "Nothing to save");

/** What a shopper sends to place an order (FR-WEB-04). */
export const shopperOrderSchema = z.object({
  name: z.string().trim().min(2, "Enter your name").max(80),
  phone: bdMobileSchema,
  districtId: z.string().refine((id) => findDistrictById(id) !== null, "Choose your district"),
  area: z.string().trim().min(2, "Enter your area or thana").max(80),
  address: z.string().trim().min(5, "Enter your full address").max(200),
  note: z.string().trim().max(300).optional(),
  lines: z
    .array(
      z.object({
        variantId: z.string().min(1).max(64),
        unitCode: z.string().min(1).max(16).optional(),
        quantity: publicQuantitySchema,
      }),
    )
    .min(1, "Your cart is empty")
    .max(50),
  /** Set when the shop verifies phone numbers. */
  otp: z
    .object({ challengeId: z.string().min(1).max(64), code: z.string().regex(/^\d{6}$/) })
    .optional(),
});
export type ShopperOrder = z.output<typeof shopperOrderSchema>;

function section(
  type: StorefrontSectionType,
  id: string,
  fields: Record<string, unknown>,
): StorefrontSection {
  return storefrontSectionSchema.parse({ id, type, ...fields });
}

/** A ready-to-publish starting design for each theme. */
export function defaultStorefrontDesign(theme: StorefrontTheme): StorefrontDesign {
  const heading: Record<StorefrontTheme, LocalText> = {
    bazaar: { en: "Everything for your home, delivered", bn: "ঘরের সব কিছু, ঘরে পৌঁছে দিই" },
    boutique: { en: "New season, new arrivals", bn: "নতুন মৌসুম, নতুন কালেকশন" },
    apothecary: { en: "Genuine medicines, delivered with care", bn: "আসল ওষুধ, যত্নে পৌঁছে দিই" },
    catalog: { en: "Browse our collection", bn: "আমাদের সংগ্রহ দেখুন" },
  };
  const color: Record<StorefrontTheme, string> = {
    bazaar: "#e4572e",
    boutique: "#1f2933",
    apothecary: "#0f766e",
    catalog: "#5b4b8a",
  };
  const sells = theme !== "catalog";
  const sections: StorefrontSection[] = [
    section("hero", "hero", {
      layout: theme === "boutique" ? "full" : theme === "apothecary" ? "text" : "split",
      heading: heading[theme],
      subheading: {
        en: sells ? "Order online, pay cash when it arrives." : "",
        bn: sells ? "অনলাইনে অর্ডার করুন, পণ্য হাতে পেয়ে টাকা দিন।" : "",
      },
      ctaLabel: { en: "Shop now", bn: "এখনই কিনুন" },
      ctaHref: "/products",
    }),
    ...(sells ? [section("highlights", "highlights", {})] : []),
    section("categories", "categories", { title: { en: "Shop by category", bn: "ক্যাটাগরি অনুযায়ী" } }),
    section("products", "newest", {
      title: { en: "New arrivals", bn: "নতুন এসেছে" },
      source: "newest",
      limit: 8,
    }),
    section("contact", "contact", { title: { en: "Visit or call us", bn: "আসুন বা ফোন করুন" } }),
  ];
  return storefrontDesignSchema.parse({ theme, primaryColor: color[theme], sections });
}
